import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search } from "lucide-react";

const API = '/api';

export default function HistoryPage() {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    axios.get(`${API}/summaries`)
      .then(res => setSummaries(res.data))
      .catch(() => toast.error("FAILED TO LOAD HISTORY"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = summaries.filter(s => 
    s.video_title.toLowerCase().includes(search.toLowerCase()) || 
    s.channel_name.toLowerCase().includes(search.toLowerCase()) ||
    (s.tags && s.tags.some(t => t.toLowerCase().includes(search.toLowerCase())))
  );

  return (
    <div className="anim-in r-full" style={{ maxWidth: 900 }} data-testid="history-page">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 32, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.2em", color: "#CC0000", marginBottom: 6 }}>ARCHIVE / RECORDS</div>
          <h1 style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "2rem", fontWeight: 700, color: "#111", margin: 0, lineHeight: 1 }}>HISTORY</h1>
          <div style={{ height: 3, background: "#111", marginTop: 10, width: 64 }} />
        </div>
        <div style={{ position: "relative", width: 280, maxWidth: "100%" }}>
          <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#888" }} />
          <input 
            data-testid="history-search-input"
            type="text" 
            placeholder="SEARCH TITLE, CHANNEL, OR TAG..." 
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="ind-input"
            style={{ paddingLeft: 36, fontSize: "0.65rem", textTransform: "uppercase" }}
          />
        </div>
      </div>

      <div style={{ border: "2px solid #111", background: "#fff" }} className="table-scroll-wrap">
        <div style={{ minWidth: 600 }}>
          {/* Header */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 140px 90px", background: "#111" }}>
            {["VIDEO / CHANNEL", "TOPICS", "CREATED", ""].map(h => (
              <div key={h} style={{ padding: "8px 16px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#888", letterSpacing: "0.14em" }}>{h}</div>
            ))}
          </div>

          {loading ? (
            [1,2,3].map(i => (
              <div key={i} style={{ display: "flex", padding: "16px", borderBottom: "1px solid #eee", opacity: 0.4, gap: 16 }}>
                <div style={{ flex: 1, height: 12, background: "#eee" }} />
                <div style={{ width: 100, height: 12, background: "#eee" }} />
                <div style={{ width: 60, height: 12, background: "#eee" }} />
              </div>
            ))
          ) : filtered.length === 0 ? (
            <div style={{ padding: "40px 24px", textAlign: "center" }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", color: "#bbb", letterSpacing: "0.14em" }}>
                {search ? "NO MATCHES FOUND" : "ARCHIVE EMPTY"}
              </div>
            </div>
          ) : (
            filtered.map((s, i) => (
              <div 
                key={s.id} 
                data-testid={`history-item-${s.id}`}
                className="anim-in"
                style={{ 
                  display: "grid", gridTemplateColumns: "1fr 160px 140px 90px", 
                  borderBottom: i < filtered.length - 1 ? "1px solid #e8e8e4" : "none",
                  animationDelay: `${i * 0.03}s`,
                  transition: "background 0.1s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#F9F9F7"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ padding: "16px 16px", minWidth: 0, display: "flex", gap: 12, alignItems: "center" }}>
                  {s.thumbnail_url && (
                    <img src={s.thumbnail_url} alt="" style={{ width: 64, height: 36, objectFit: "cover", filter: "grayscale(100%)", opacity: 0.8, flexShrink: 0 }} />
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", fontWeight: 600, color: "#111", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 4 }}>
                      {s.video_title.toUpperCase()}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#888" }}>{s.channel_name.toUpperCase()}</span>
                      {s.auto_generated && (
                        <span style={{ background: "#111", color: "#fff", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.45rem", padding: "1px 4px", letterSpacing: "0.08em" }}>AUTO</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Topics Column */}
                <div style={{ padding: "16px 16px", display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                  {(s.tags && s.tags.length > 0 ? s.tags : ["Uncategorized"]).map(tag => (
                    <span key={tag} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.5rem", background: "#f0f0f0", color: "#555", padding: "2px 6px", border: "1px solid #ddd" }}>
                      {tag.toUpperCase()}
                    </span>
                  ))}
                </div>

                <div style={{ padding: "16px 16px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", color: "#888", display: "flex", alignItems: "center" }}>
                  {new Date(s.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }).toUpperCase()}
                </div>
                
                <div style={{ padding: "16px 16px", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                  <Link to={`/summary/${s.id}`} style={{ textDecoration: "none" }} data-testid={`view-summary-${s.id}`}>
                    <button 
                      style={{ 
                        background: "transparent", border: "1px solid #111", cursor: "pointer", 
                        padding: "4px 12px", fontFamily: "'JetBrains Mono', monospace", 
                        fontSize: "0.55rem", fontWeight: 700, color: "#111", transition: "all 0.1s"
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = "#111"; e.currentTarget.style.color = "#fff"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#111"; }}
                    >
                      READ →
                    </button>
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
